import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import type { AssetClass, BenchmarkImageSortFields, ImageView, PairScoreView, SortDirection } from '../../generated';
import type { TagRef } from '../../generated';
import { listBenchmarkImagesV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdImagesGet } from '../../generated';
import { extractTotalCount } from '../../utils';

/*
 * GET /v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}/images, live behind the API's `custom-benchmarks` flag.
 * It answers with the generated `ImageView`; `toCustomBenchmarkImage` is the one place it is mapped. Tests answer
 * this path with `tests/mocks/benchmark-handlers.ts`.
 */

export type BenchmarkImageSortBy = BenchmarkImageSortFields;

export type BenchmarkImageSortDirection = SortDirection;

/**
 * A benchmark image as the listing returns it: the image, plus its `pair_score` when a category was asked for.
 * `pair_score.scored` is `false` while the pair is unscored, was never requested, or its pool is empty.
 */
export type BenchmarkImageResponse = ImageView;

/** An image of a custom benchmark, mapped for the UI. */
export interface CustomBenchmarkImage {
  id: string;
  /** The uploaded file name. */
  name: string;
  /** Small rendition for grid tiles. It 404s until the image is resized, which happens on its first scoring. */
  thumbnailUrl: string | null;
  /** Large rendition for big grid tiles, `null` when the image has none. It 404s until resized, like `thumbnailUrl`. */
  largeThumbnailUrl: string | null;
  /** The full-size original, the fallback while the renditions do not exist yet. */
  originalUrl: string | null;
  /**
   * The benchmark's score for this image under the requested product category. `null` without a category, and
   * while the pair is not scored: the image's own `vizit_score` scores it against its own category instead, so it
   * is never used here.
   */
  vizitScore: number | null;
  /** Where scoring the image under the requested category stands; `null` without a category. */
  scoringStatus: PairScoreView['scoring_status'];
  classification: AssetClass | null;
  /** When the image last changed, which anchors how long a scoring may stay pending. */
  updatedAt: string | null;
  workspaceId: string | null;
  favorite: boolean;
  artifacts: ImageView['artifacts'];
  tags: TagRef[] | null;
}

export function toCustomBenchmarkImage(image: BenchmarkImageResponse): CustomBenchmarkImage {
  // All three URLs are signed locally, without checking S3, so they are never a sign the file has arrived.
  const originalUrl = image.url ?? null;
  const pairScore = image.pair_score ?? null;

  return {
    id: image.id,
    name: image.name,
    thumbnailUrl: image.url_360 ?? originalUrl,
    largeThumbnailUrl: image.url_760 ?? null,
    originalUrl,
    vizitScore: pairScore?.scored ? pairScore.vizit_score : null,
    scoringStatus: pairScore?.scoring_status ?? null,
    classification: image.classification ?? null,
    updatedAt: image.updated_at ?? image.created_at,
    workspaceId: image.workspace_id ?? null,
    favorite: !!image.favorite,
    artifacts: image.artifacts ?? null,
    tags: image.tags ?? null,
  };
}

export interface ListBenchmarkImagesQueryVariables {
  workspace_id: string;
  benchmark_id: string;
  /** Adds each image's `pair_score` for this category. The API then caps `limit` at 100. */
  product_category_id?: string;
  limit?: number;
  offset?: number;
  sort_by?: BenchmarkImageSortBy;
  sort_direction?: BenchmarkImageSortDirection;
  include_artifacts?: string[];
}

export interface ListBenchmarkImagesQuery {
  images: CustomBenchmarkImage[];
  fetchedAt?: number;
  /** Every image in the benchmark, from the `Vizit-Total-Count` header. */
  totalCount: number;
}

async function listBenchmarkImages({ workspace_id, benchmark_id, ...query }: ListBenchmarkImagesQueryVariables) {
  const { data, response } = await listBenchmarkImagesV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdImagesGet({
    path: { workspace_id, benchmark_id },
    query,
    throwOnError: true,
  });
  const images = (data ?? []).map(toCustomBenchmarkImage);

  return { images, totalCount: extractTotalCount(response, images.length), fetchedAt: Date.now() };
}

export const useListBenchmarkImagesQuery = <TData = ListBenchmarkImagesQuery, TError = unknown>(
  variables: ListBenchmarkImagesQueryVariables,
  options?: Omit<UseQueryOptions<ListBenchmarkImagesQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<ListBenchmarkImagesQuery, TError, TData>({
    queryKey: useListBenchmarkImagesQuery.getKey(variables),
    queryFn: () => listBenchmarkImages(variables),
    ...options,
  });
};

useListBenchmarkImagesQuery.getKey = (variables: ListBenchmarkImagesQueryVariables) => [
  'ListBenchmarkImages',
  variables,
];

useListBenchmarkImagesQuery.fetcher = (variables: ListBenchmarkImagesQueryVariables) => () =>
  listBenchmarkImages(variables);
